import type { MapDownloadContent, MapDownloadPlacement } from '@repo/types/analytic-events/maps-analytic-events';

export const MAP_DOWNLOAD_ATTRIBUTION_CHANNEL = 'web_maps';
export const MAP_DOWNLOAD_ATTRIBUTION_MEDIUM = 'owned_web';
export const MAP_DOWNLOAD_ATTRIBUTION_CAMPAIGN = 'maps_to_app';
export const MOD_CLIP_DOWNLOAD_ATTRIBUTION_CHANNEL = 'game_page_mod_clips';

/** Channels whose `/download` visits carry their attribution into the `app_download` event. */
export const attributedDownloadChannels: readonly string[] = [
    MAP_DOWNLOAD_ATTRIBUTION_CHANNEL,
    MOD_CLIP_DOWNLOAD_ATTRIBUTION_CHANNEL,
];

export const downloadAttributionParamNames = [
    'acquisition_id',
    'acquisition_channel',
    'acquisition_placement',
    'acquisition_page',
    'map_id',
    'mod_uuid',
    'video_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'referral_code',
] as const;

export interface MapDownloadContext {
    mapId?: string | null;
    pagePath?: string | null;
    titleId?: string | null;
}

export interface MapDownloadAttribution extends MapDownloadContext {
    acquisitionId?: string;
    placement: MapDownloadPlacement;
    utmContent?: MapDownloadContent;
}

export interface ModClipDownloadAttribution {
    modUuid: string;
    pagePath: string;
    titleId: string;
    videoId: string;
}

function modifyUrl(value: string, modify: (url: URL) => void): string {
    const isAbsolute = URL.canParse(value);
    const url = new URL(value, 'https://wand.com');
    modify(url);

    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}

function applyMapDownloadContext(url: URL, context: MapDownloadContext): void {
    if (context.titleId) url.searchParams.set('title_id', context.titleId);
    if (context.mapId) url.searchParams.set('map_id', context.mapId);
    if (context.pagePath) url.searchParams.set('acquisition_page', context.pagePath);
}

export function appendMapDownloadContext(url: string, context: MapDownloadContext): string {
    return modifyUrl(url, target => applyMapDownloadContext(target, context));
}

export function appendMapDownloadAttribution(url: string, attribution: MapDownloadAttribution): string {
    return modifyUrl(url, target => {
        if (attribution.acquisitionId) target.searchParams.set('acquisition_id', attribution.acquisitionId);
        target.searchParams.set('acquisition_channel', MAP_DOWNLOAD_ATTRIBUTION_CHANNEL);
        target.searchParams.set('acquisition_placement', attribution.placement);
        target.searchParams.set('utm_source', MAP_DOWNLOAD_ATTRIBUTION_CHANNEL);
        target.searchParams.set('utm_medium', MAP_DOWNLOAD_ATTRIBUTION_MEDIUM);
        target.searchParams.set('utm_campaign', MAP_DOWNLOAD_ATTRIBUTION_CAMPAIGN);
        target.searchParams.set('utm_content', attribution.utmContent ?? attribution.placement);

        applyMapDownloadContext(target, attribution);
    });
}

export function appendModClipDownloadAttribution(url: string, attribution: ModClipDownloadAttribution): string {
    return modifyUrl(url, target => {
        target.searchParams.set('acquisition_channel', MOD_CLIP_DOWNLOAD_ATTRIBUTION_CHANNEL);
        target.searchParams.set('acquisition_placement', 'mod_clip_end_card');
        target.searchParams.set('acquisition_page', attribution.pagePath);
        target.searchParams.set('title_id', attribution.titleId);
        target.searchParams.set('mod_uuid', attribution.modUuid);
        target.searchParams.set('video_id', attribution.videoId);
    });
}

export function forwardDownloadAttribution(url: string, searchParams: URLSearchParams): string {
    return modifyUrl(url, target => {
        for (const name of downloadAttributionParamNames) {
            const value = searchParams.get(name);
            if (value) target.searchParams.set(name, value);
        }
    });
}
